const UTM_KEYS = {
    source: 'utm_source',
    medium: 'utm_medium',
    campaign: 'utm_campaign',
    content: 'utm_content',
    term: 'utm_term',
};
const UTM_QUERY_KEYS = new Set(Object.values(UTM_KEYS));
const emptyUtm = {
    source: '',
    medium: '',
    campaign: '',
    content: '',
    term: '',
};
/**
 * Splits a URL string into base URL and UTM params.
 * Uses string manipulation (not `new URL()`) because URLs may contain
 * `$variables` or lack a protocol.
 */
export function parseUtmFromUrl(url) {
    if (!url) {
        return { baseUrl: '', utm: Object.assign({}, emptyUtm) };
    }
    const [beforeHash, hash] = splitOnce(url, '#');
    const [path, queryString] = splitOnce(beforeHash, '?');
    if (!queryString) {
        return {
            baseUrl: url,
            utm: Object.assign({}, emptyUtm),
        };
    }
    const params = queryString.split('&');
    const utm = Object.assign({}, emptyUtm);
    const remaining = [];
    for (const param of params) {
        const [key, ...valueParts] = param.split('=');
        const value = valueParts.join('=');
        const utmField = Object.entries(UTM_KEYS).find(([, qKey]) => qKey === key);
        if (utmField) {
            utm[utmField[0]] = decodeURIComponent(value);
        }
        else {
            remaining.push(param);
        }
    }
    let baseUrl = path;
    if (remaining.length > 0) {
        baseUrl += `?${remaining.join('&')}`;
    }
    if (hash !== undefined) {
        baseUrl += `#${hash}`;
    }
    return { baseUrl, utm };
}
/**
 * Appends non-empty UTM params to a base URL.
 * Strips any existing UTM params from the base URL first to prevent duplication.
 */
export function buildUrlWithUtm(baseUrl, utm) {
    if (!utm) {
        return baseUrl;
    }
    // Strip existing UTM params from base URL to prevent duplication
    const { baseUrl: cleanBase } = stripUtmFromUrl(baseUrl);
    const utmEntries = Object.entries(UTM_KEYS)
        .filter(([field]) => { var _a; return (_a = utm[field]) === null || _a === void 0 ? void 0 : _a.trim(); })
        .map(([field, queryKey]) => `${queryKey}=${encodeURIComponent(utm[field].trim())}`);
    if (utmEntries.length === 0) {
        return cleanBase;
    }
    const [beforeHash, hash] = splitOnce(cleanBase, '#');
    const separator = beforeHash.includes('?') ? '&' : '?';
    const result = beforeHash + separator + utmEntries.join('&');
    return hash !== undefined ? `${result}#${hash}` : result;
}
function stripUtmFromUrl(url) {
    const [beforeHash, hash] = splitOnce(url, '#');
    const [path, queryString] = splitOnce(beforeHash, '?');
    if (!queryString) {
        return { baseUrl: url };
    }
    const remaining = queryString
        .split('&')
        .filter((param) => {
        var _a;
        const key = (_a = param.split('=')[0]) !== null && _a !== void 0 ? _a : '';
        return !UTM_QUERY_KEYS.has(key);
    });
    let baseUrl = path;
    if (remaining.length > 0) {
        baseUrl += `?${remaining.join('&')}`;
    }
    if (hash !== undefined) {
        baseUrl += `#${hash}`;
    }
    return { baseUrl };
}
function splitOnce(str, delimiter) {
    const idx = str.indexOf(delimiter);
    if (idx === -1) {
        return [str, undefined];
    }
    return [str.slice(0, idx), str.slice(idx + 1)];
}
